import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, X, WifiOff, Wifi, ArrowRight, ArrowLeft, Lock, Check, Save, ChevronRight } from 'lucide-react';
import { User } from '../types';
import {
  Booking, StageHistoryEntry, STAGE_ORDER, CustomerSpecificDetails,
  fetchBookings, fetchStageHistory, moveStage, isLifecycleConnected,
  fetchCustomerSpecificDetails, saveCustomerSpecificDetails, fetchEnquiries,
} from '../utils/lifecycleApi';

interface BookingTrackerProps { user: User; }

const EVENT_DAY_INDEX = STAGE_ORDER.indexOf('Event Execution');

const STAGE_CHECKLISTS: Record<string, { task: string; who: string; where: string; how: string }[]> = {
  'Customer Document & Booking': [
    { task:'Advance payment received and receipt saved', who:'COO / Shivam', where:'Office / Bank', how:'Collect cash/UPI, save receipt in customer folder. Note amount and date.' },
    { task:'CSR number entered from Master Service Tracker', who:'Admin', where:'Customer Details tab + Excel', how:'Generate CSR in Master Service Tracker Excel, then paste into Studio Quote Ref field in Customer Details tab.' },
    { task:'Booking confirmation message sent to customer', who:'Employee / COO', where:'WhatsApp', how:'Use standard booking confirmation template. Include: CSR number, event date, package name, advance amount received, next steps.' },
    { task:'Customer added to WhatsApp group', who:'Employee', where:'WhatsApp', how:'Create group: [CustomerName]_ShivamStudio. Add customer + relevant team members.' },
    { task:'Studio T&C shared with customer', who:'COO', where:'WhatsApp / Email', how:'Share T&C PDF. Get acknowledgement (reply/screenshot). Save in customer folder.' },
    { task:'Customer Specific Document created', who:'COO / Admin', where:'Word Document', how:'Use Customer Specific Document template. Fill: customer info, event dates, venue, package, POC, special requirements. Save with CSR number as filename.' },
    { task:'Enquiry Tracker updated to Confirmed', who:'Employee', where:'Lifecycle → Enquiry Tracker', how:'Find the enquiry → Edit → Status: Confirmed. Booking should already be linked.' },
  ],
  'Contractor/Vendor Assignment': [
    { task:'Contractor availability confirmed for event dates', who:'COO / Shivam', where:'Phone / WhatsApp', how:'Call/message contractor. Confirm they are free on event date. Get written confirmation.' },
    { task:'Nature of work shared with contractor', who:'COO', where:'WhatsApp', how:'Share: event date, venue, type of work (photo/video/cine/drone), reporting time, customer name (first name only at this stage).' },
    { task:'Contractor rate confirmed and agreed', who:'COO / Shivam', where:'WhatsApp / Call', how:'Confirm rate as per our rate catalog. If different, get approval from Shivam before agreeing.' },
    { task:'PP vendor confirmed (Gunabh / Shankar)', who:'COO', where:'WhatsApp / Call', how:'Confirm PP vendor availability and rate. Share event type and expected deliverables.' },
    { task:'Album vendor confirmed (if applicable)', who:'COO', where:'Phone / WhatsApp', how:'Confirm album type, paper, size, sheet count, rate. Get timeline.' },
    { task:'Work order reference created', who:'Admin', where:'Master Service Tracker Excel', how:'Enter WO number in Excel. Link to CSR. Note contractor, vendor, confirmed rates.' },
    { task:'Post-Production Tracker updated with vendor', who:'Employee', where:'Lifecycle → PP Tracker', how:'Open PP Tracker → select this booking → enter vendor name for each deliverable.' },
  ],
  'Readiness & Planning': [
    { task:'Second meeting scheduled (Premium/Exclusive)', who:'COO', where:'Office / Call', how:'For premium packages, schedule a planning meeting. Agenda: event list, shot list, storyboard, music, POC.' },
    { task:'Event schedule / itinerary received', who:'Employee', where:'WhatsApp / Email', how:'Request event itinerary from customer. Fill into Event Tracker format. Share with team.' },
    { task:'Shot list finalized', who:'COO / Team', where:'Customer Specific Document', how:'Update shot list section: standard shots + preferred/special shots discussed in second meeting.' },
    { task:'Venue and location details confirmed', who:'Employee', where:'WhatsApp / Google Maps', how:'Confirm venue address, parking, entry point, contact at venue. Save in Customer Specific Document.' },
    { task:'POC details confirmed and saved', who:'COO', where:'Customer Details tab', how:'Save customer POC name + number. Save contractor POC. Fill in Customer Details tab in Booking Tracker.' },
    { task:'Equipment checklist reviewed', who:'Team Lead', where:'Event Audit Checklist', how:'Open Lifecycle → Event Audit Checklist → select this booking → check all equipment items.' },
    { task:'Readiness Document prepared', who:'COO', where:'Word Document', how:'Use Readiness Doc template. Fill: event list, shot list, storyboard, special requirements, logistics, team roles, equipment needed.' },
    { task:'Team briefed on event details', who:'Shivam / COO', where:'Office / WhatsApp group', how:'Share event date, venue, reporting time, customer name, special instructions, roles assigned.' },
  ],
  'Event Execution': [
    { task:'Reached venue 30-60 minutes before start', who:'Full Team', where:'Event Venue', how:'Leave office/home with buffer. Parking arranged. All equipment loaded and checked before leaving.' },
    { task:'Equipment set up and tested at venue', who:'Photographer / Videographer', where:'Venue', how:'Check all cameras, batteries, memory cards, audio, drone. Test shots taken. Confirm all working.' },
    { task:'POC met and event flow confirmed', who:'Team Lead', where:'Venue', how:'Meet customer POC immediately on arrival. Confirm event timeline, special moments, any last minute changes.' },
    { task:'Standard + preferred shot list followed', who:'Full Team', where:'Venue', how:'Keep shot list accessible. Check off key moments: rituals, family portraits, couple shots, décor, food, crowd.' },
    { task:'BTS and social media content captured', who:'Designated team member', where:'Venue', how:'Capture behind-the-scenes photos/videos. Reels content. Keep aside separately on memory card.' },
    { task:'WhatsApp group updated during event', who:'Team Lead', where:'WhatsApp group', how:'Send 2-3 updates to studio WhatsApp group during event. Post-event: "Event wrapped, heading back."' },
    { task:'Data backed up immediately after event', who:'Team Lead', where:'Laptop / External Drive', how:'Copy all memory cards to primary + backup drive before leaving venue or immediately on return. Verify count.' },
    { task:'Manager/Shivam informed of wrap', who:'Team Lead', where:'WhatsApp', how:'Send wrap message: "Event complete. X hours shot. Data backed up. Back at [location]."' },
  ],
  'Post-Production': [
    { task:'Raw footage verified and checked', who:'Admin / COO', where:'Office', how:'Check all data received from contractor. Count files, verify quality. If issue found, contact contractor immediately.' },
    { task:'Data shared with PP vendor with briefing', who:'COO', where:'WeTransfer / Hard Drive', how:'Share footage with PP vendor. Include briefing note: customer name, event type, references, deliverable list, deadline.' },
    { task:'Post-Production Tracker updated', who:'Employee', where:'Lifecycle → PP Tracker', how:'Update status for each deliverable as it progresses. Mark In Progress when work starts.' },
    { task:'Draft reviews done within correction limit', who:'COO', where:'WhatsApp / Email', how:'Share draft with customer. Track correction count. Maximum corrections as per T&C. Do not exceed without approval from Shivam.' },
    { task:'Album order placed with vendor (if applicable)', who:'COO', where:'Phone / WhatsApp', how:'Share selected album design with album vendor. Confirm paper, size, sheets, quantity, delivery timeline.' },
    { task:'Final deliverables received and quality checked', who:'COO / Shivam', where:'Office', how:'Review each deliverable before sending to customer. Check quality, branding, no watermarks, correct format.' },
  ],
  'Final Delivery': [
    { task:'Soft copy delivered to customer', who:'Employee / COO', where:'WeTransfer / Google Drive / Pendrive', how:'Share link or physical drive as per package. Include all deliverables. Confirm receipt with customer.' },
    { task:'Final invoice shared and payment collected', who:'COO / Admin', where:'WhatsApp / Office', how:'Share final invoice. Collect balance payment. Update Cost Management. Mark paid in system.' },
    { task:'Customer sign-off / approval received', who:'COO', where:'WhatsApp', how:'Get written confirmation from customer that they received and are satisfied. Screenshot or message saved.' },
    { task:'Hard copy / pendrive delivered (if applicable)', who:'Employee', where:'Customer address / Office pickup', how:'Deliver physical copy. Customer signs acknowledgement. Update delivery in Customer Specific Document.' },
    { task:'Cost Management updated — all payments marked', who:'Admin', where:'Lifecycle → Cost Management', how:'Mark all contractor/vendor payments as paid. Verify totals match work orders.' },
  ],
  'Post-Event': [
    { task:'Thank you message sent to customer', who:'COO / Shivam', where:'WhatsApp / Email', how:'Send personalised thank you. Mention the event. Request feedback. Include social media collaboration request.' },
    { task:'Customer feedback collected', who:'COO', where:'WhatsApp / Google Form', how:'Ask for written feedback. Save screenshot. If negative, escalate to Shivam. If positive, request Google review.' },
    { task:'Social media posted as per consent', who:'Employee / COO', where:'Instagram / Facebook', how:'Check social media consent in Customer Details tab. If Yes — post with agreed caption/tag. If No — do not post.' },
    { task:'Lesson learned noted in records', who:'Shivam / COO', where:'Internal document', how:'Note any issues, delays, customer complaints, what worked well. Share in team review.' },
    { task:'Files archived and drive cleared', who:'Admin', where:'External drive / Cloud', how:'Archive customer folder with CSR name. Clear working drive. Update Master Service Tracker as CLOSED.' },
    { task:'Master Service Tracker updated to CLOSED', who:'Admin', where:'Excel — Master Service Tracker', how:'Mark the CSR row as Closed. Add final amount, delivery date, feedback rating.' },
  ],
};
const SECOND_MEETING_OPTIONS = ['Not Scheduled', 'Scheduled', 'Completed'];
// No longer a manual dropdown (see computeReadinessStatus) — kept as the
// reference list of valid values the derived status can take.
const READINESS_OPTIONS = ['Not Started', 'In Progress', 'Completed'];
const READINESS_STAGE = 'Readiness & Planning';

// Reads straight from localStorage rather than component state, so it's
// always accurate regardless of which stage the Stage Tracking tab currently
// has open — Readiness Status shouldn't depend on which tab you last looked at.
function computeReadinessStatus(bookingId: number): string {
  const key = `ss_stage_checks_${bookingId}_${READINESS_STAGE}`;
  let saved: Record<string, boolean> = {};
  try { saved = JSON.parse(localStorage.getItem(key) || '{}'); } catch { saved = {}; }
  const items = STAGE_CHECKLISTS[READINESS_STAGE] || [];
  if (items.length === 0) return 'Not Started';
  const doneCount = items.filter(i => saved[i.task]).length;
  if (doneCount === 0) return 'Not Started';
  if (doneCount === items.length) return 'Completed';
  return 'In Progress';
}

// Payment log — Feature #8. Kept in localStorage (no backend column exists
// for this yet) as a running, dated record of what's actually been collected,
// separate from the single advance_received figure saved to the CSR. Each
// entry is tied to whichever paper document it actually belongs to — not
// tagged by tax category, just by what it references, so the log stays
// descriptive record-keeping rather than a tax classification the tool makes.
type PaymentRefType = 'Invoice No' | 'Quotation No' | 'Enquiry No' | 'CSR No' | 'Work Order No' | 'Delivery Challan No' | 'Purchase Invoice No' | 'Other';
const PAYMENT_REF_TYPES: PaymentRefType[] = ['Invoice No', 'Quotation No', 'Enquiry No', 'CSR No', 'Work Order No', 'Delivery Challan No', 'Purchase Invoice No', 'Other'];
interface PaymentLogEntry { id: string; date: string; amount: number; refType: PaymentRefType; refNumber: string; note: string; recordedBy: string; recordedAt: string; }
function loadPaymentLog(bookingId: number): PaymentLogEntry[] {
  try { return JSON.parse(localStorage.getItem(`ss_payment_log_${bookingId}`) || '[]'); } catch { return []; }
}
function savePaymentLog(bookingId: number, entries: PaymentLogEntry[]) {
  localStorage.setItem(`ss_payment_log_${bookingId}`, JSON.stringify(entries));
}

const emptyDetailsForm = {
  // Contact & Identity
  customer_poc_name: '', customer_poc_contact: '', contractor_poc_name: '', contractor_poc_contact: '',
  billing_name: '', email_id: '', social_media_id: '',
  // Service-specific
  family_side: '', bride_name: '', groom_name: '', family_name: '',
  birthday_person_name: '', company_name: '', event_further_details: '',
  // Booking & Package
  second_meeting_date: '', second_meeting_status: 'Not Scheduled',
  final_package: '', addons: '',
  total_quoted_amount: '', advance_received: '', balance_due: '',
  // Work Order & Docs
  work_order_number: '', purchase_invoice_number: '', delivery_challan_number: '',
  cust_doc_prep_start_date: '', before_event_checklist_shared: 'Not Done',
  // Planning
  shooting_dates: '', coverage_list: '', detailed_event_list: '',
  plan_for_shoot_notes: '', action_items_for_studio: '',
  // Status
  readiness_status: 'Not Started', special_requirements: '', comments: '',
};

export default function BookingTracker({ user }: BookingTrackerProps) {
  const [connected] = useState(isLifecycleConnected());
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Booking | null>(null);
  // Feature #6: Customer Details is the front door now — Stage Tracking is
  // the deeper/less-frequent view, so it comes second.
  const [tab, setTab] = useState<'details' | 'stage'>('details');

  // Stage tab state
  const [history, setHistory] = useState<StageHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [moveTo, setMoveTo] = useState('');
  const [stageChecks, setStageChecks] = useState<Record<string, boolean>>({});

  const loadStageChecks = (bookingId: number, stage: string): Record<string, boolean> => {
    const key = `ss_stage_checks_${bookingId}_${stage}`;
    try { return JSON.parse(localStorage.getItem(key) || '{}'); } catch { return {}; }
  };
  const saveStageCheck = (bookingId: number, stage: string, task: string, done: boolean) => {
    const key = `ss_stage_checks_${bookingId}_${stage}`;
    const current = loadStageChecks(bookingId, stage);
    localStorage.setItem(key, JSON.stringify({ ...current, [task]: done }));
    setStageChecks(prev => ({ ...prev, [task]: done }));
    // Feature #9: Readiness Status is derived from this exact checklist, so
    // keep it live the moment a Readiness & Planning item is (un)checked.
    if (stage === READINESS_STAGE) {
      setDetailsForm(prev => ({ ...prev, readiness_status: computeReadinessStatus(bookingId) }));
    }
  };
  const [note, setNote] = useState('');
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState('');

  // Feature #10: which stage-section is expanded in the Customer Details tab.
  // Defaults to whichever stage the booking is actually in.
  const [expandedSection, setExpandedSection] = useState<string>(STAGE_ORDER[0]);

  // Customer Details tab state
  const [detailsForm, setDetailsForm] = useState(emptyDetailsForm);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [detailsSavedAt, setDetailsSavedAt] = useState<string | null>(null);
  // Booking only stores enquiry_id, not the enquiry's own WENQ ref — fetched
  // separately so it can sit alongside the other reference numbers.
  const [enquiryRefNumber, setEnquiryRefNumber] = useState<string | null>(null);

  // Feature #8: payment log — dated record of what's actually been collected,
  // each entry tied to whichever document it belongs to. Kept alongside
  // (not instead of) the single advance_received figure.
  const [paymentLog, setPaymentLog] = useState<PaymentLogEntry[]>([]);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [newPayment, setNewPayment] = useState<{ date: string; amount: string; refType: PaymentRefType; refNumber: string; note: string }>({
    date: new Date().toISOString().slice(0, 10), amount: '', refType: 'Invoice No', refNumber: '', note: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setBookings(await fetchBookings());
    } catch (e: any) {
      setError(e.message || 'Could not load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (connected) load(); else setLoading(false); }, [connected, load]);

  // Feature #6: fields currently showing a value pulled from the linked
  // quote that hasn't been explicitly confirmed or edited yet.
  const [autoFilledFields, setAutoFilledFields] = useState<Set<string>>(new Set());
  const confirmField = (name: string) => setAutoFilledFields(prev => { const n = new Set(prev); n.delete(name); return n; });
  const AutoFilledTag = ({ name }: { name: string }) => !autoFilledFields.has(name) ? null : (
    <button type="button" onClick={() => confirmField(name)}
      style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: 'var(--teal)', background: 'rgba(56,178,172,0.1)', border: '1px solid var(--teal)', borderRadius: 100, padding: '1px 7px', cursor: 'pointer' }}
      title="Pulled from the linked quote — click to confirm it's correct">
      auto-filled · tap to confirm
    </button>
  );

  // Shared by openBooking (initial open) and the "Customer Details" tab
  // button (manual reload) so the two never drift out of sync.
  const loadDetailsForBooking = async (b: Booking) => {
    setDetailsLoading(true);
    setDetailsError('');
    try {
      const d = await fetchCustomerSpecificDetails(b.id);
      // Check for linked planner quote data to auto-fill empty fields
      const links = JSON.parse(localStorage.getItem('ss_booking_quote_links') || '{}');
      const lq = links[b.id];
      const derivedReadiness = computeReadinessStatus(b.id);
      // Feature #6: which fields got their value from the linked quote rather
      // than a saved/typed value — shown as "auto-filled, tap to confirm" so
      // it's never silently trusted without a human look.
      const autoFilled = new Set<string>();
      if (d) {
        const total = d.total_quoted_amount || (lq?.quotedAmount ? String(lq.quotedAmount) : '');
        const advance = d.advance_received || '';
        if (!d.customer_poc_contact && lq?.customerPhone) autoFilled.add('customer_poc_contact');
        if (!d.final_package && lq) autoFilled.add('final_package');
        if (!d.addons && lq?.addons) autoFilled.add('addons');
        if (!d.total_quoted_amount && lq?.quotedAmount) autoFilled.add('total_quoted_amount');
        if (!(d as any).email_id && lq?.customerEmail) autoFilled.add('email_id');
        setDetailsForm({
          customer_poc_name: d.customer_poc_name || '',
          customer_poc_contact: d.customer_poc_contact || lq?.customerPhone || '',
          contractor_poc_name: d.contractor_poc_name || '', contractor_poc_contact: d.contractor_poc_contact || '',
          second_meeting_date: d.second_meeting_date || '', second_meeting_status: d.second_meeting_status || 'Not Scheduled',
          final_package: d.final_package || (lq ? `${lq.serviceName} — ${lq.productName} (${lq.qualityTier})` : ''),
          addons: d.addons || lq?.addons || '',
          total_quoted_amount: total,
          advance_received: advance,
          // Feature #8: auto-calculated rather than trusting a possibly-stale saved value
          balance_due: computeBalanceDue(total, advance),
          readiness_status: derivedReadiness,
          special_requirements: d.special_requirements || '', comments: d.comments || '',
          billing_name: (d as any).billing_name || '',
          email_id: (d as any).email_id || lq?.customerEmail || '',
          social_media_id: (d as any).social_media_id || '',
          family_side: (d as any).family_side || '',
          bride_name: (d as any).bride_name || '',
          groom_name: (d as any).groom_name || '',
          family_name: (d as any).family_name || '',
          birthday_person_name: (d as any).birthday_person_name || '',
          company_name: (d as any).company_name || '',
          event_further_details: (d as any).event_further_details || '',
          work_order_number: (d as any).work_order_number || '',
          purchase_invoice_number: (d as any).purchase_invoice_number || '',
          delivery_challan_number: (d as any).delivery_challan_number || '',
          cust_doc_prep_start_date: (d as any).cust_doc_prep_start_date || '',
          before_event_checklist_shared: (d as any).before_event_checklist_shared || 'Not Done',
          shooting_dates: (d as any).shooting_dates || '',
          coverage_list: (d as any).coverage_list || '',
          detailed_event_list: (d as any).detailed_event_list || '',
          plan_for_shoot_notes: (d as any).plan_for_shoot_notes || '',
          action_items_for_studio: (d as any).action_items_for_studio || '',
        });
        setDetailsSavedAt(d.updated_at);
      } else {
        // No saved details yet — pre-fill from linked quote if available
        const total = lq?.quotedAmount ? String(lq.quotedAmount) : '';
        if (lq?.customerPhone) autoFilled.add('customer_poc_contact');
        if (lq) autoFilled.add('final_package');
        if (lq?.addons) autoFilled.add('addons');
        if (lq?.quotedAmount) autoFilled.add('total_quoted_amount');
        if (lq?.customerEmail) autoFilled.add('email_id');
        setDetailsForm({
          ...emptyDetailsForm,
          customer_poc_contact: lq?.customerPhone || '',
          email_id: lq?.customerEmail || '',
          final_package: lq ? `${lq.serviceName} — ${lq.productName} (${lq.qualityTier})` : '',
          addons: lq?.addons || '',
          total_quoted_amount: total,
          balance_due: computeBalanceDue(total, ''),
          readiness_status: derivedReadiness,
        });
        setDetailsSavedAt(null);
      }
      setAutoFilledFields(autoFilled);
    } catch (e: any) {
      setDetailsError(e.message || 'Could not load customer details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const openBooking = async (b: Booking) => {
    setSelected(b);
    setTab('details');
    setMoveTo('');
    setNote('');
    setMoveError('');
    setExpandedSection(b.current_stage || STAGE_ORDER[0]);
    setStageChecks(loadStageChecks(b.id, b.current_stage));
    setPaymentLog(loadPaymentLog(b.id));
    setShowAddPayment(false);
    setHistoryLoading(true);
    setEnquiryRefNumber(null);
    // Details and stage history load together now, regardless of which tab shows first.
    loadDetailsForBooking(b);
    // Booking only stores enquiry_id — resolve the WENQ ref by matching it
    // within its own service's enquiry list. Best-effort: if it fails, the
    // ref just doesn't show, nothing else depends on it.
    fetchEnquiries(b.service_type).then(list => {
      const match = list.find(e => e.id === b.enquiry_id);
      if (match) setEnquiryRefNumber(match.ref_number);
    }).catch(() => {});
    try {
      setHistory(await fetchStageHistory(b.id));
    } catch (e: any) {
      setMoveError(e.message || 'Could not load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openDetailsTab = async (b: Booking) => {
    setTab('details');
    await loadDetailsForBooking(b);
  };

  const handleAddPayment = () => {
    if (!selected || !newPayment.amount || Number(newPayment.amount) <= 0) return;
    const entry: PaymentLogEntry = {
      id: `pay_${Date.now()}`, date: newPayment.date, amount: Number(newPayment.amount),
      refType: newPayment.refType, refNumber: newPayment.refNumber, note: newPayment.note,
      recordedBy: user.name, recordedAt: new Date().toISOString(),
    };
    const updated = [...paymentLog, entry];
    setPaymentLog(updated);
    savePaymentLog(selected.id, updated);
    setNewPayment({ date: new Date().toISOString().slice(0, 10), amount: '', refType: 'Invoice No', refNumber: '', note: '' });
    setShowAddPayment(false);
  };

  const handleDeletePayment = (id: string) => {
    if (!selected) return;
    const updated = paymentLog.filter(p => p.id !== id);
    setPaymentLog(updated);
    savePaymentLog(selected.id, updated);
  };

  const paymentLogTotal = paymentLog.reduce((s, p) => s + p.amount, 0);

  // Feature #8: single source of truth for Balance Due — always total minus
  // advance, so the field can never drift out of sync the way a manually
  // typed figure could.
  function computeBalanceDue(total: string, advance: string): string {
    const t = Number(total), a = Number(advance);
    if (!total || isNaN(t)) return '';
    const bal = t - (isNaN(a) ? 0 : a);
    return String(bal);
  }

  const setTotalAmount = (v: string) => setDetailsForm(prev => ({ ...prev, total_quoted_amount: v, balance_due: computeBalanceDue(v, prev.advance_received) }));
  const setAdvanceAmount = (v: string) => setDetailsForm(prev => ({ ...prev, advance_received: v, balance_due: computeBalanceDue(prev.total_quoted_amount, v) }));

  const handleSaveDetails = async () => {
    if (!selected) return;
    setDetailsSaving(true);
    setDetailsError('');
    try {
      const saved = await saveCustomerSpecificDetails(selected.id, detailsForm);
      setDetailsSavedAt(saved.updated_at);
    } catch (e: any) {
      setDetailsError(e.message || 'Save failed');
    } finally {
      setDetailsSaving(false);
    }
  };

  const handleMove = async () => {
    if (!selected || !moveTo) return;
    setMoving(true);
    setMoveError('');
    try {
      const updated = await moveStage(selected.id, moveTo, note || undefined);
      setSelected(updated);
      setBookings(prev => prev.map(b => b.id === updated.id ? updated : b));
      setHistory(await fetchStageHistory(updated.id));
      setStageChecks(loadStageChecks(updated.id, updated.current_stage));
      setExpandedSection(updated.current_stage);
      setMoveTo('');
      setNote('');
      // Jump straight into Customer Details for the new stage, rather than
      // leaving the person on Stage Tracking to click over themselves.
      setTab('details');
      await loadDetailsForBooking(updated);
    } catch (e: any) {
      setMoveError(e.message || 'Move failed');
    } finally {
      setMoving(false);
    }
  };

  if (!connected) {
    return (
      <div style={{ padding: 28, maxWidth: 700 }}>
        <h1>Booking &amp; Stage Tracker</h1>
        <div className="alert alert-amber" style={{ marginTop: 16 }}>
          <WifiOff size={16} />
          <span>Not connected to the Lifecycle Management backend. Try signing out and back in.</span>
        </div>
      </div>
    );
  }

  const currentIndex = selected ? STAGE_ORDER.indexOf(selected.current_stage) : -1;

  const readinessItems = STAGE_CHECKLISTS[READINESS_STAGE] || [];
  const readinessChecks = selected ? loadStageChecks(selected.id, READINESS_STAGE) : {};
  const readinessDone = readinessItems.filter(i => readinessChecks[i.task]).length;

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '7px 14px', borderRadius: 6, fontSize: 12.5, fontWeight: active ? 700 : 500,
    background: active ? 'rgba(183,121,31,0.15)' : 'transparent',
    color: active ? 'var(--gold)' : 'var(--text-secondary)',
    border: active ? '1px solid rgba(183,121,31,0.3)' : '1px solid transparent',
    cursor: 'pointer',
  });

  // Feature #10: Customer Details tab, grouped into collapsible sections that
  // mirror the 7-stage lifecycle. The section for the booking's current stage
  // opens by default (set in openBooking/handleMove); others stay reachable
  // since earlier-stage data often still needs editing after moving on.
  const Section = ({ stage, children, note }: { stage: string; children?: React.ReactNode; note?: string }) => {
    const isOpen = expandedSection === stage;
    const isCurrent = selected?.current_stage === stage;
    const stageNum = STAGE_ORDER.indexOf(stage) + 1;
    return (
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
        <button type="button" onClick={() => setExpandedSection(isOpen ? '' : stage)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
            background: isCurrent ? 'rgba(183,121,31,0.08)' : 'var(--bg-subtle)', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
          <span style={{ background: isCurrent ? 'var(--gold)' : '#9CA3AF', color: '#fff', borderRadius: 100, width: 18, height: 18,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>{stageNum}</span>
          <span style={{ flex: 1, fontWeight: 700, fontSize: 13, color: isCurrent ? 'var(--gold)' : 'var(--text-primary)' }}>{stage}</span>
          {isCurrent && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.04em' }}>CURRENT STAGE</span>}
          <ChevronRight size={14} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s', color: 'var(--text-muted)', flexShrink: 0 }} />
        </button>
        {isOpen && <div style={{ padding: 14 }}>{children || <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{note}</p>}</div>}
      </div>
    );
  };

  return (
    <div style={{ padding: 28, maxWidth: 1200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1>Booking &amp; Stage Tracker — Wedding</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Wifi size={13} color="var(--teal)" /> Live · {bookings.length} booking{bookings.length === 1 ? '' : 's'}
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}><AlertTriangle size={15} /> <span>{error}</span></div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>Loading…</div>
      ) : bookings.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
          <p>No bookings yet. Create one from a Confirmed enquiry in the Enquiry Tracker.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>CSR No.</th><th>Customer</th><th>Event Dates</th><th>Current Stage</th><th>Event Passed?</th><th></th>
              </tr></thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => openBooking(b)}>
                    <td style={{ fontWeight: 600, color: 'var(--gold)' }}>{b.csr_number}</td>
                    <td>{b.customer_name}</td>
                    <td>{b.event_dates || '—'}</td>
                    <td><span className="badge badge-blue">{b.current_stage}</span></td>
                    <td>{b.event_passed ? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Yes — locked before Event Execution</span> : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No</span>}</td>
                    <td style={{ textAlign: 'right', color: 'var(--gold)', fontSize: 12 }}>View →</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={() => setSelected(null)}>
          <div className="card card-lg" style={{ width: 680, maxHeight: '88vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="card-header">
              <div>
                <h3>{selected.csr_number} — {selected.customer_name}</h3>
                {(() => {
                  const links = JSON.parse(localStorage.getItem('ss_booking_quote_links') || '{}');
                  const lq = links[selected.id];
                  if (!lq) return null;
                  return <div style={{fontSize:11,color:'var(--teal)',marginTop:2}}>🔗 Linked to {lq.quoteNumber} · {lq.serviceName} {lq.productName} · ₹{(lq.quotedAmount||0).toLocaleString('en-IN')}</div>;
                })()}
              </div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setSelected(null)}><X size={15} /></button>
            </div>

            {/* Booking Refs */}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12, padding:'8px 12px', background:'var(--bg-subtle)', borderRadius:6, fontSize:12 }}>
              <span><strong>CSR:</strong> {selected.csr_number}</span>
              {enquiryRefNumber && <span><strong>Enquiry:</strong> {enquiryRefNumber}</span>}
              {selected.quote_ref_system && <span><strong>System Quote:</strong> {selected.quote_ref_system}</span>}
              {selected.quote_ref_studio && <span><strong>Studio Quote:</strong> {selected.quote_ref_studio}</span>}
              {selected.invoice_ref_system && <span><strong>System Invoice:</strong> {selected.invoice_ref_system}</span>}
              {selected.invoice_ref_studio && <span><strong>Studio Invoice:</strong> {selected.invoice_ref_studio}</span>}
              <span><strong>Service:</strong> {selected.service_type}</span>
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
              <button style={tabBtnStyle(tab === 'details')} onClick={() => openDetailsTab(selected)}>Customer Details</button>
              <button style={tabBtnStyle(tab === 'stage')} onClick={() => setTab('stage')}>Stage Tracking</button>
            </div>

            {tab === 'stage' && (
              <>
                {/* Stage stepper */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 20, flexWrap: 'wrap' }}>
                  {STAGE_ORDER.map((stage, i) => {
                    const isCurrent = i === currentIndex;
                    const isPast = i < currentIndex;
                    const isLocked = selected.event_passed === 1 && i < EVENT_DAY_INDEX;
                    return (
                      <React.Fragment key={stage}>
                        <div style={{
                          padding: '5px 9px', borderRadius: 6, fontSize: 10.5, fontWeight: isCurrent ? 700 : 500,
                          background: isCurrent ? 'rgba(183,121,31,0.18)' : isPast ? 'rgba(56,178,172,0.12)' : 'var(--bg-subtle)',
                          color: isCurrent ? 'var(--gold)' : isPast ? 'var(--teal)' : isLocked ? 'var(--text-muted)' : 'var(--text-secondary)',
                          border: isCurrent ? '1px solid rgba(183,121,31,0.4)' : '1px solid transparent',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          {isPast && <Check size={10} />}
                          {isLocked && !isPast && <Lock size={10} />}
                          {stage}
                        </div>
                        {i < STAGE_ORDER.length - 1 && <ArrowRight size={11} style={{ opacity: 0.3, flexShrink: 0 }} />}
                      </React.Fragment>
                    );
                  })}
                </div>

                {selected.event_passed === 1 && (
                  <div className="alert alert-amber" style={{ marginBottom: 16, fontSize: 12.5 }}>
                    <Lock size={13} /> <span>This event has happened — stages before Event Execution are locked and can't be moved back to.</span>
                  </div>
                )}

                {/* Move stage control */}
                <div className="form-row" style={{ marginBottom: 8 }}>
                  <div className="form-group">
                    <label>Move to stage</label>
                    <select className="select" value={moveTo} onChange={e => setMoveTo(e.target.value)}>
                      <option value="">Select a stage…</option>
                      {STAGE_ORDER.map(s => <option key={s} value={s} disabled={s === selected.current_stage}>{s}{s === selected.current_stage ? ' (current)' : ''}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Note (optional)</label>
                    <input className="input" placeholder="Why is this moving?" value={note} onChange={e => setNote(e.target.value)} />
                  </div>
                </div>
                {moveError && <div className="alert alert-error" style={{ marginBottom: 12 }}><AlertTriangle size={14} /> <span>{moveError}</span></div>}
                <button className="btn btn-primary" disabled={!moveTo || moving} onClick={handleMove} style={{ marginBottom: 24 }}>
                  {moving ? 'Moving…' : <><ArrowLeft size={13} style={{ marginRight: 2 }} /><ArrowRight size={13} style={{ marginRight: 6 }} /> Move Stage</>}
                </button>

                {/* Stage Checklist for current stage */}
                {selected && STAGE_CHECKLISTS[selected.current_stage] && (
                  <div style={{ marginTop:16, marginBottom:16 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:'var(--gold)', marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
                      📋 {selected.current_stage} — What to do at this stage
                      <span style={{ fontSize:11, fontWeight:400, color:'var(--text-muted)' }}>
                        ({STAGE_CHECKLISTS[selected.current_stage].filter(t => stageChecks[t.task]).length}/{STAGE_CHECKLISTS[selected.current_stage].length} done)
                      </span>
                    </div>
                    {STAGE_CHECKLISTS[selected.current_stage].map(item => (
                      <div key={item.task} style={{ marginBottom:8, padding:'10px 14px', borderRadius:8,
                        background: stageChecks[item.task] ? '#F0FDF4' : '#FAFAFA',
                        border: `1px solid ${stageChecks[item.task] ? '#86EFAC' : '#E5E7EB'}` }}>
                        <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                          <input type="checkbox" checked={!!stageChecks[item.task]}
                            onChange={e => saveStageCheck(selected.id, selected.current_stage, item.task, e.target.checked)}
                            style={{ marginTop:3, cursor:'pointer', flexShrink:0 }} />
                          <div style={{ flex:1 }}>
                            <div style={{ fontWeight:600, fontSize:13, color: stageChecks[item.task] ? '#065F46' : '#1F2937',
                              textDecoration: stageChecks[item.task] ? 'line-through' : 'none', marginBottom:4 }}>{item.task}</div>
                            <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:3 }}>
                              <span style={{ fontSize:11, color:'#6B7280' }}>👤 <strong>Who:</strong> {item.who}</span>
                              <span style={{ fontSize:11, color:'#6B7280' }}>📍 <strong>Where:</strong> {item.where}</span>
                            </div>
                            <div style={{ fontSize:11, color:'#6B7280' }}>💡 <strong>How:</strong> {item.how}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* History */}
                <h4 style={{ fontSize: 13, marginBottom: 10, color: 'var(--text-secondary)' }}>Stage History</h4>
                {historyLoading ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {history.map(h => (
                      <div key={h.id} style={{ fontSize: 12.5, borderLeft: '2px solid var(--gold)', paddingLeft: 10 }}>
                        <div>
                          {h.from_stage ? <>{h.from_stage} <ArrowRight size={10} style={{ display: 'inline', margin: '0 3px' }} /> </> : ''}
                          <strong>{h.to_stage}</strong>
                        </div>
                        <div style={{ color: 'var(--text-muted)' }}>
                          {h.changed_by_name || 'Unknown'} · {new Date(h.changed_at).toLocaleString()}
                          {h.note ? ` — ${h.note}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {tab === 'details' && (
              <>
                {detailsLoading ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>
                ) : (
                  <>
                    <Section stage="Customer Document & Booking">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Customer POC Name</label>
                          <input className="input" placeholder="Family point of contact" value={detailsForm.customer_poc_name} onChange={e => setDetailsForm({ ...detailsForm, customer_poc_name: e.target.value })} />
                        </div>
                        <div className="form-group">
                          <label>Customer POC Contact <AutoFilledTag name="customer_poc_contact" /></label>
                          <input className="input" value={detailsForm.customer_poc_contact} onChange={e => { setDetailsForm({ ...detailsForm, customer_poc_contact: e.target.value }); confirmField('customer_poc_contact'); }} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Final Package (confirmed) <AutoFilledTag name="final_package" /></label>
                        <input className="input" value={detailsForm.final_package} onChange={e => { setDetailsForm({ ...detailsForm, final_package: e.target.value }); confirmField('final_package'); }} />
                      </div>
                      <div className="form-group">
                        <label>Add-ons <AutoFilledTag name="addons" /></label>
                        <input className="input" value={detailsForm.addons} onChange={e => { setDetailsForm({ ...detailsForm, addons: e.target.value }); confirmField('addons'); }} />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Total Quoted Amount <AutoFilledTag name="total_quoted_amount" /></label>
                          <input className="input" type="number" value={detailsForm.total_quoted_amount} onChange={e => { setTotalAmount(e.target.value); confirmField('total_quoted_amount'); }} />
                        </div>
                        <div className="form-group">
                          <label>Advance Received</label>
                          <input className="input" type="number" value={detailsForm.advance_received} onChange={e => setAdvanceAmount(e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label>Balance Due <span style={{fontSize:10,color:'var(--text-muted)',fontWeight:400}}>(auto)</span></label>
                          <input className="input" type="number" value={detailsForm.balance_due} disabled style={{opacity:0.7,cursor:'not-allowed'}} title="Auto-calculated: Total Quoted − Advance Received" />
                        </div>
                      </div>

                      {/* Feature #8: payment log — dated, each entry tied to whichever document it belongs to */}
                      <div style={{ margin: '4px 0 14px' }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                            Payment Log
                            {paymentLog.length > 0 && <span style={{color:'var(--text-muted)',fontWeight:400}}> · ₹{paymentLogTotal.toLocaleString('en-IN')} across {paymentLog.length} entr{paymentLog.length===1?'y':'ies'}</span>}
                          </div>
                          <button type="button" className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={() => setShowAddPayment(v => !v)}>{showAddPayment ? 'Cancel' : '+ Add Payment'}</button>
                        </div>
                        {paymentLog.length > 0 && (
                          <div style={{ marginBottom: 8, border:'1px solid var(--border)', borderRadius: 6, overflow:'hidden' }}>
                            {paymentLog.map(p => (
                              <div key={p.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 10px', fontSize:12, borderBottom:'1px solid var(--border)', background:'#fff', flexWrap:'wrap' }}>
                                <span style={{ color:'var(--text-muted)', minWidth:80 }}>{p.date}</span>
                                <strong style={{minWidth:80}}>₹{p.amount.toLocaleString('en-IN')}</strong>
                                {p.refNumber
                                  ? <span className="badge badge-blue" style={{fontSize:10}}>{p.refType}: {p.refNumber}</span>
                                  : <span className="badge badge-blue" style={{fontSize:10}}>{p.refType}</span>}
                                <span style={{ color:'var(--text-muted)', flex:1 }}>{p.note}</span>
                                <span style={{ color:'var(--text-muted)', fontSize:11 }}>{p.recordedBy}</span>
                                <button type="button" onClick={() => handleDeletePayment(p.id)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',padding:2,display:'flex'}} title="Remove entry"><X size={12}/></button>
                              </div>
                            ))}
                          </div>
                        )}
                        {showAddPayment && (
                          <div style={{ padding: 12, background: 'var(--bg-subtle)', borderRadius: 6 }}>
                            <div className="form-row">
                              <div className="form-group"><label style={{fontSize:11}}>Date</label>
                                <input className="input" type="date" value={newPayment.date} onChange={e=>setNewPayment({...newPayment,date:e.target.value})}/>
                              </div>
                              <div className="form-group"><label style={{fontSize:11}}>Amount</label>
                                <input className="input" type="number" value={newPayment.amount} onChange={e=>setNewPayment({...newPayment,amount:e.target.value})}/>
                              </div>
                            </div>
                            <div className="form-row">
                              <div className="form-group"><label style={{fontSize:11}}>Reference Type <span style={{fontWeight:400,color:'var(--text-muted)'}}>— whichever applies</span></label>
                                <select className="select" value={newPayment.refType} onChange={e=>setNewPayment({...newPayment,refType:e.target.value as PaymentRefType})}>
                                  {PAYMENT_REF_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                                </select>
                              </div>
                              <div className="form-group"><label style={{fontSize:11}}>Reference Number <span style={{fontWeight:400,color:'var(--text-muted)'}}>(optional)</span></label>
                                <input className="input" placeholder="e.g. SS-INV-260805-1234" value={newPayment.refNumber} onChange={e=>setNewPayment({...newPayment,refNumber:e.target.value})}/>
                              </div>
                            </div>
                            <div className="form-group"><label style={{fontSize:11}}>Note (optional)</label>
                              <input className="input" placeholder="e.g. Advance, 2nd installment..." value={newPayment.note} onChange={e=>setNewPayment({...newPayment,note:e.target.value})}/>
                            </div>
                            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                              <button type="button" className="btn btn-primary btn-sm" disabled={!newPayment.amount || Number(newPayment.amount)<=0} onClick={handleAddPayment}>Add Entry</button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div style={{borderTop:'1px solid var(--border)',paddingTop:14,marginTop:4}}>
                        <div style={{fontWeight:700,fontSize:12.5,color:'var(--gold)',marginBottom:10}}>Identity & Billing</div>
                        <div className="form-row">
                          <div className="form-group"><label>Billing Name</label>
                            <input className="input" placeholder="Name on invoice" value={detailsForm.billing_name} onChange={e=>setDetailsForm({...detailsForm,billing_name:e.target.value})}/>
                          </div>
                          <div className="form-group"><label>Email ID <AutoFilledTag name="email_id" /></label>
                            <input className="input" type="email" value={detailsForm.email_id} onChange={e=>{setDetailsForm({...detailsForm,email_id:e.target.value}); confirmField('email_id');}}/>
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group"><label>Social Media ID / Handle</label>
                            <input className="input" placeholder="e.g. @akrutichavre" value={detailsForm.social_media_id} onChange={e=>setDetailsForm({...detailsForm,social_media_id:e.target.value})}/>
                          </div>
                          <div className="form-group"><label>Work Order Number</label>
                            <input className="input" placeholder="e.g. 3SSWO20261#PT" value={detailsForm.work_order_number} onChange={e=>setDetailsForm({...detailsForm,work_order_number:e.target.value})}/>
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group"><label>Purchase Invoice Number (Contractor)</label>
                            <input className="input" placeholder="Contractor PO/Invoice ref" value={detailsForm.purchase_invoice_number} onChange={e=>setDetailsForm({...detailsForm,purchase_invoice_number:e.target.value})}/>
                          </div>
                          <div className="form-group"><label>Delivery Challan Number</label>
                            <input className="input" placeholder="If goods/equipment moved with a challan" value={detailsForm.delivery_challan_number} onChange={e=>setDetailsForm({...detailsForm,delivery_challan_number:e.target.value})}/>
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group"><label>Cust Specific Doc Prep Start Date <span style={{fontSize:11,color:'var(--text-muted)'}}>Rule: 20 days before event</span></label>
                            <input className="input" type="date" value={detailsForm.cust_doc_prep_start_date} onChange={e=>setDetailsForm({...detailsForm,cust_doc_prep_start_date:e.target.value})}/>
                          </div>
                        </div>
                      </div>

                      {selected && (() => {
                        const svc = selected.service_type?.toLowerCase() || '';
                        const isWedding = svc.includes('wedding') || svc.includes('engagement') || svc.includes('anniversary');
                        const isBirthday = svc.includes('birthday');
                        const isOffice = svc.includes('office');
                        const isAny = svc.includes('any');
                        if (!isWedding && !isBirthday && !isOffice && !isAny) return null;
                        return (
                        <div style={{borderTop:'1px solid var(--border)',paddingTop:14,marginTop:4}}>
                          <div style={{fontWeight:700,fontSize:12.5,color:'var(--gold)',marginBottom:10}}>Service-Specific Details</div>
                          {isWedding && (<>
                            <div className="form-row">
                              <div className="form-group"><label>Family Side</label>
                                <select className="select" value={detailsForm.family_side} onChange={e=>setDetailsForm({...detailsForm,family_side:e.target.value})}>
                                  {['','Bride Side','Groom Side','Both Side'].map(o=><option key={o}>{o}</option>)}
                                </select>
                              </div>
                              <div className="form-group"><label>Bride Name</label>
                                <input className="input" value={detailsForm.bride_name} onChange={e=>setDetailsForm({...detailsForm,bride_name:e.target.value})}/>
                              </div>
                            </div>
                            <div className="form-row">
                              <div className="form-group"><label>Groom Name</label>
                                <input className="input" value={detailsForm.groom_name} onChange={e=>setDetailsForm({...detailsForm,groom_name:e.target.value})}/>
                              </div>
                              <div className="form-group"><label>Family Name</label>
                                <input className="input" placeholder="e.g. Chavre & Chourasia Family" value={detailsForm.family_name} onChange={e=>setDetailsForm({...detailsForm,family_name:e.target.value})}/>
                              </div>
                            </div>
                          </>)}
                          {isBirthday && (
                            <div className="form-group"><label>Birthday Person Name</label>
                              <input className="input" value={detailsForm.birthday_person_name} onChange={e=>setDetailsForm({...detailsForm,birthday_person_name:e.target.value})}/>
                            </div>
                          )}
                          {isOffice && (
                            <div className="form-group"><label>Company Name</label>
                              <input className="input" value={detailsForm.company_name} onChange={e=>setDetailsForm({...detailsForm,company_name:e.target.value})}/>
                            </div>
                          )}
                          {isAny && (
                            <div className="form-group"><label>Event Further Details</label>
                              <textarea className="textarea" rows={3} value={detailsForm.event_further_details} onChange={e=>setDetailsForm({...detailsForm,event_further_details:e.target.value})}/>
                            </div>
                          )}
                        </div>
                        );
                      })()}
                    </Section>

                    <Section stage="Contractor/Vendor Assignment">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Contractor POC Name</label>
                          <input className="input" value={detailsForm.contractor_poc_name} onChange={e => setDetailsForm({ ...detailsForm, contractor_poc_name: e.target.value })} />
                        </div>
                        <div className="form-group">
                          <label>Contractor POC Contact</label>
                          <input className="input" value={detailsForm.contractor_poc_contact} onChange={e => setDetailsForm({ ...detailsForm, contractor_poc_contact: e.target.value })} />
                        </div>
                      </div>
                    </Section>

                    <Section stage="Readiness & Planning">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Second Meeting Date</label>
                          <input className="input" type="date" value={detailsForm.second_meeting_date} onChange={e => setDetailsForm({ ...detailsForm, second_meeting_date: e.target.value })} />
                        </div>
                        <div className="form-group">
                          <label>Second Meeting Status</label>
                          <select className="select" value={detailsForm.second_meeting_status} onChange={e => setDetailsForm({ ...detailsForm, second_meeting_status: e.target.value })}>
                            {SECOND_MEETING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group"><label>Shooting Dates (confirmed)</label>
                          <input className="input" placeholder="e.g. 12-Mar, 13-Mar, 14-Mar" value={detailsForm.shooting_dates} onChange={e=>setDetailsForm({...detailsForm,shooting_dates:e.target.value})}/>
                        </div>
                        <div className="form-group"><label>Before Event Checklist Shared to Customer</label>
                          <select className="select" value={detailsForm.before_event_checklist_shared} onChange={e=>setDetailsForm({...detailsForm,before_event_checklist_shared:e.target.value})}>
                            {['Not Done','Shared','Acknowledged by Customer'].map(o=><option key={o}>{o}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="form-group"><label>Coverage List (what to cover)</label>
                        <textarea className="textarea" rows={2} placeholder="e.g. Mehendi, Haldi, Barat, Varmala, Phere, Reception" value={detailsForm.coverage_list} onChange={e=>setDetailsForm({...detailsForm,coverage_list:e.target.value})}/>
                      </div>
                      <div className="form-group">
                        <label>Detailed Event List <span style={{fontSize:11,color:'var(--text-muted)'}}>Customer shares in any format — paste/type here</span></label>
                        <textarea className="textarea" rows={5} placeholder="Paste customer's event programme here — dates, functions, venues, timings..." value={detailsForm.detailed_event_list} onChange={e=>setDetailsForm({...detailsForm,detailed_event_list:e.target.value})}/>
                      </div>
                      <div className="form-group"><label>Plan for Shoot Notes</label>
                        <textarea className="textarea" rows={3} placeholder="Key shots, storyboard notes, special requirements from second meeting..." value={detailsForm.plan_for_shoot_notes} onChange={e=>setDetailsForm({...detailsForm,plan_for_shoot_notes:e.target.value})}/>
                      </div>
                      <div className="form-group"><label>Action Items for Studio</label>
                        <textarea className="textarea" rows={3} placeholder="e.g. Arrange walkie-talkie, get AI QR code, confirm drone permit..." value={detailsForm.action_items_for_studio} onChange={e=>setDetailsForm({...detailsForm,action_items_for_studio:e.target.value})}/>
                      </div>
                      <div className="form-group"><label>Special Requirements</label>
                        <textarea className="textarea" value={detailsForm.special_requirements} onChange={e => setDetailsForm({ ...detailsForm, special_requirements: e.target.value })} />
                      </div>

                      {/* Feature #9: read-only, derived from the checklist itself rather than a separate manual field */}
                      <div className="form-group">
                        <label>Readiness Status <span style={{fontSize:10,color:'var(--text-muted)',fontWeight:400}}>(auto — from this stage's checklist)</span></label>
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <span className={detailsForm.readiness_status==='Completed'?'badge badge-teal':detailsForm.readiness_status==='In Progress'?'badge badge-amber':'badge badge-blue'}>{detailsForm.readiness_status}</span>
                          <span style={{fontSize:11.5,color:'var(--text-muted)'}}>{readinessDone}/{readinessItems.length} Readiness &amp; Planning checklist items done</span>
                        </div>
                      </div>
                    </Section>

                    <Section stage="Event Execution" note="No customer-detail fields for this stage — tracked live via the checklist above and the Event Audit Checklist." />
                    <Section stage="Post-Production" note="No customer-detail fields for this stage — tracked via the Post-Production Tracker." />
                    <Section stage="Final Delivery" note="No customer-detail fields for this stage — tracked via Cost Management (payments) and the checklist above." />
                    <Section stage="Post-Event" note="No customer-detail fields for this stage yet — social media consent/posting fields are designed but waiting on the pending DB migration. Use Lesson Learned for notes in the meantime." />

                    <div style={{borderTop:'1px solid var(--border)',paddingTop:14,marginTop:4,marginBottom:14}}>
                      <div className="form-group">
                        <label>Comments</label>
                        <textarea className="textarea" value={detailsForm.comments} onChange={e => setDetailsForm({ ...detailsForm, comments: e.target.value })} />
                      </div>
                    </div>

                    {detailsError && <div className="alert alert-error" style={{ marginBottom: 12 }}><AlertTriangle size={14} /> <span>{detailsError}</span></div>}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button className="btn btn-primary" disabled={detailsSaving} onClick={handleSaveDetails}>
                        <Save size={13} /> {detailsSaving ? 'Saving…' : 'Save Customer Details'}
                      </button>
                      {detailsSavedAt && <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Last saved {new Date(detailsSavedAt).toLocaleString()}</span>}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
